// ─────────────────────────────────────────────────────────────
// FluxoMed — Engine de Validação de Pipeline
// Pure functions — sem side effects, sem chamadas ao banco
// ─────────────────────────────────────────────────────────────

export const PIPELINE_STATUS_CFG = {
  rascunho:        { label: "Rascunho",            cor: "var(--muted)",  bg: "var(--surface-3)", icone: "◎"  },
  homologacao:     { label: "Homologação",        cor: "var(--warn-ink)",        bg: "var(--warn-soft)",       icone: "🔬" },
  implantacao:     { label: "Implantação",        cor: "var(--cat-violet-ink)",  bg: "var(--cat-violet-soft)", icone: "⚙"  },
  pronto_producao: { label: "Pronto p/ Produção", cor: "var(--cat-teal-ink)",    bg: "var(--cat-teal-soft)",   icone: "🚀" },
  producao:        { label: "Em Produção",        cor: "var(--success-ink)",    bg: "var(--success-soft)",    icone: "✅" },
  pausado:         { label: "Pausado",            cor: "var(--danger-ink)",     bg: "var(--danger-soft)",     icone: "⏸"  },
};

export const PIPELINE_STAGES = [
  "rascunho", "homologacao", "implantacao", "pronto_producao", "producao", "pausado",
];

// Próximo estágio natural no pipeline (null = sem avanço automático)
export const NEXT_STAGE = {
  rascunho:        "homologacao",
  homologacao:     "implantacao",
  implantacao:     "pronto_producao",
  pronto_producao: "producao",
  producao:        null,
  pausado:         null,
};

// ─────────────────────────────────────────────────────────────
// getClienteHealth(cliente, implantacao)
//
// Retorna:
//   pendencias.configuracao  — campos básicos não preenchidos
//   pendencias.homologacao   — checklist / status de homologação
//   pendencias.implantacao   — checklist / status de implantação
//   podeLancamento           — true quando todos os gates de produção passam
//   healthLevel              — "verde" | "amarelo" | "vermelho"
//   totalPendencias          — número total de pendências
// ─────────────────────────────────────────────────────────────
export function getClienteHealth(cliente, implantacao) {
  const pendencias = {
    configuracao: [],
    homologacao:  [],
    implantacao:  [],
  };

  // ── Configuração ──────────────────────────────────────────
  if (!cliente.medico_nome?.trim())
    pendencias.configuracao.push("Nome do médico não preenchido");
  if (!cliente.secretaria_nome?.trim())
    pendencias.configuracao.push("Nome da secretária não preenchido");
  if (!cliente.secretaria_whatsapp?.trim())
    pendencias.configuracao.push("WhatsApp da secretária não informado");
  if (!cliente.instancia_evolution?.trim())
    pendencias.configuracao.push("Instância Evolution não configurada");
  if (!cliente.supabase_project?.trim())
    pendencias.configuracao.push("Projeto Supabase do bot não configurado");

  // ── Homologação ───────────────────────────────────────────
  const homChecklist = Array.isArray(cliente.homologacao_checklist)
    ? cliente.homologacao_checklist
    : [];
  const homPendentes = homChecklist.filter((i) => !i.ok).length;
  if (homPendentes > 0)
    pendencias.homologacao.push(`${homPendentes} item(s) do checklist de homologação pendentes`);
  if (cliente.homologacao_status !== "aprovado")
    pendencias.homologacao.push("Homologação não aprovada");

  // ── Implantação ───────────────────────────────────────────
  if (!implantacao) {
    pendencias.implantacao.push("Implantação não iniciada (abra a aba Implantação)");
  } else {
    const implChecklist = Array.isArray(implantacao.checklist)
      ? implantacao.checklist
      : [];
    const implPendentes = implChecklist.filter((i) => !i.concluido).length;
    if (implPendentes > 0)
      pendencias.implantacao.push(`${implPendentes} item(s) do checklist de implantação pendentes`);
    if (implantacao.status !== "concluida")
      pendencias.implantacao.push("Status de implantação não marcado como concluída");
  }

  // ── Gate de produção ──────────────────────────────────────
  const podeLancamento = (
    cliente.homologacao_status === "aprovado" &&
    !!implantacao &&
    implantacao.status === "concluida" &&
    Array.isArray(implantacao.checklist) &&
    implantacao.checklist.length > 0 &&
    implantacao.checklist.every((i) => i.concluido)
  );

  // ── Saúde visual ──────────────────────────────────────────
  const totalPendencias =
    pendencias.configuracao.length +
    pendencias.homologacao.length +
    pendencias.implantacao.length;

  const healthLevel =
    totalPendencias === 0 ? "verde" :
    totalPendencias <= 3  ? "amarelo" : "vermelho";

  return { pendencias, podeLancamento, healthLevel, totalPendencias };
}

// ─────────────────────────────────────────────────────────────
// canAdvanceTo(cliente, implantacao, targetStage)
//
// Verifica se o cliente pode avançar para o estágio alvo.
// Retorna { allowed: bool, motivo: string | null }
// ─────────────────────────────────────────────────────────────
export function canAdvanceTo(cliente, implantacao, targetStage) {
  switch (targetStage) {
    case "homologacao":
      return { allowed: true, motivo: null };

    case "implantacao":
      if (cliente.homologacao_status !== "aprovado")
        return { allowed: false, motivo: "Homologação ainda não aprovada" };
      return { allowed: true, motivo: null };

    case "pronto_producao": {
      const { podeLancamento, pendencias } = getClienteHealth(cliente, implantacao);
      if (!podeLancamento) {
        const bloqueios = [
          ...pendencias.homologacao,
          ...pendencias.implantacao,
        ];
        return { allowed: false, motivo: bloqueios[0] || "Pendências em aberto" };
      }
      return { allowed: true, motivo: null };
    }

    case "producao":
      if (cliente.status !== "pronto_producao")
        return { allowed: false, motivo: "Cliente deve estar em 'Pronto p/ Produção'" };
      return { allowed: true, motivo: null };

    default:
      return { allowed: true, motivo: null };
  }
}
